import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  HomeIcon,
  NewspaperIcon,
  CalendarIcon,
  UserGroupIcon,
  SupportIcon,
  GlobeIcon,
  IdentificationIcon,
  DatabaseIcon,
  OfficeBuildingIcon,
  LightBulbIcon,
  BookOpenIcon,
  BadgeCheckIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  MenuIcon,
  XIcon
} from '@heroicons/react/outline';
import cx from 'classnames';
import { useAuth } from '../Context/AuthContext';

const navigation = [
  { name: 'Dashboard', href: 'dashboard', icon: HomeIcon },
  { name: 'Profil Gampong', href: 'profile', icon: IdentificationIcon },
  { name: 'Berita', href: 'berita', icon: NewspaperIcon },
  { name: 'Kegiatan', href: 'kegiatan', icon: CalendarIcon },
  { name: 'Perangkat Gampong', href: 'perangkat', icon: UserGroupIcon },
  { name: 'Layanan', href: 'layanan', icon: SupportIcon },
  { name: 'Geografi', href: 'geografi', icon: GlobeIcon },
  { name: 'SDM', href: 'sdm', icon: IdentificationIcon },
  { name: 'SDG', href: 'sdg', icon: DatabaseIcon },
  { name: 'Pemerintahan', href: 'pemerintahan', icon: OfficeBuildingIcon },
  { name: 'Visi Misi', href: 'visimisi', icon: LightBulbIcon },
  { name: 'Sejarah', href: 'sejarah', icon: BookOpenIcon },
  { name: 'Pencapaian', href: 'pencapaian', icon: BadgeCheckIcon },
  { name: 'Qanun', href: 'qanun', icon: ShieldCheckIcon },
  { name: 'Dokumen', href: 'documents', icon: DocumentDuplicateIcon },
  { name: 'Gallery', href: 'gallery', icon: NewspaperIcon },
];

export default function CMSLayout({ children }) {
  const { slug } = useParams();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, userData, logout, isAdmin } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = `/${slug}/cms/login`;
    } catch (error) {
      console.error("Logout error", error);
    }
  };

  const dynamicNavigation = [...navigation];
  if (isAdmin) {
    dynamicNavigation.push({ name: 'Manajemen User', href: 'users', icon: ShieldCheckIcon });
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar */}
      <div
        className={cx(
          "fixed inset-0 z-40 lg:hidden",
          sidebarOpen ? "block" : "hidden"
        )}
      >
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-white shadow-xl transition-all duration-300">
          <div className="flex items-center justify-between h-16 px-4 bg-primary text-white">
            <span className="text-xl font-bold italic tracking-wider">CMS Desa</span>
            <button onClick={() => setSidebarOpen(false)}>
              <XIcon className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {dynamicNavigation.map((item) => {
              const isActive = location.pathname.includes(item.href);
              return (
                <Link
                  key={item.name}
                  to={`/${slug}/cms/${item.href}`}
                  className={cx(
                    "flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200",
                    isActive 
                      ? "bg-primary text-white shadow-lg shadow-primary/20" 
                      : "text-gray-600 hover:bg-gray-100 hover:text-primary"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={cx("w-5 h-5 mr-3", isActive ? "text-white" : "text-gray-400 group-hover:text-primary")} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-72">
          <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-200 shadow-sm">
            <div className="flex items-center h-20 px-6 border-b border-gray-100 mb-4">
              <div className="bg-primary p-2 rounded-xl mr-3">
                <ShieldCheckIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-extrabold text-gray-800 tracking-tight">Gampong<span className="text-primary italic">CMS</span></span>
            </div>
            <nav className="flex-1 px-4 pb-4 space-y-1.5 overflow-y-auto custom-scrollbar">
              {dynamicNavigation.map((item) => {
                const isActive = location.pathname.includes(item.href);
                return (
                  <Link
                    key={item.name}
                    to={`/${slug}/cms/${item.href}`}
                    className={cx(
                      "group flex items-center px-4 py-3.5 text-sm font-semibold rounded-2xl transition-all duration-300",
                      isActive 
                        ? "bg-primary text-white shadow-xl shadow-primary/30 transform scale-[1.02]" 
                        : "text-gray-500 hover:bg-gray-50 hover:text-primary"
                    )}
                  >
                    <item.icon className={cx(
                      "w-5 h-5 mr-4 transition-colors duration-300", 
                      isActive ? "text-white" : "text-gray-400 group-hover:text-primary"
                    )} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-500 rounded-xl hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <XIcon className="w-5 h-5 mr-3" />
                Keluar CMS / Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        <header className="flex items-center justify-between h-20 px-4 bg-white border-b border-gray-100 lg:px-8">
          <button
            className="p-2 -ml-2 text-gray-600 lg:hidden hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon className="w-6 h-6" />
          </button>
          
          <div className="flex items-center">
             <h1 className="text-xl font-bold text-gray-800">Panel Manajemen Desa</h1>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center px-3 py-1.5 bg-green-50 rounded-full border border-green-100">
               <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
               <span className="text-xs font-bold text-green-700 capitalize">Role: {userData?.role || 'User'}</span>
            </div>
            <div className="flex items-center space-x-3 p-1 rounded-xl transition-colors">
              <div className="w-10 h-10 bg-indigo-100 text-indigo-700 flex items-center justify-center rounded-xl font-bold text-sm uppercase">
                {userData?.role?.substring(0, 2) || 'AD'}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-bold text-gray-800 leading-tight">Admin Gampong</p>
                <p className="text-xs text-gray-500 leading-tight">{currentUser?.email}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-[#F8FAFC] custom-scrollbar">
          <div className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #E2E8F0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #CBD5E0;
        }
      `}</style>
    </div>
  );
}
